import React, { useState, useEffect } from "react";
import { Building2, ArrowRight, CheckCircle2, Loader2, Send } from "lucide-react";
import Swal from "sweetalert2";

export default function StepBranchSelection({ formData, updateFormData, onPrev }) {
  const BOOKING_BASE = "/api/booking/";

  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [services, setServices] = useState([]);
  const [dates, setDates] = useState([]);
  const [times, setTimes] = useState([]);
  const [advisers, setAdvisers] = useState([]);

  const [loadingProvinces, setLoadingProvinces] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingDealers, setLoadingDealers] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const [loadingDates, setLoadingDates] = useState(false);
  const [loadingTimes, setLoadingTimes] = useState(false);
  const [loadingAdvisers, setLoadingAdvisers] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize Provinces
  useEffect(() => {
    const fetchProvinces = async () => {
      setLoadingProvinces(true);
      try {
        const res = await fetch(`${BOOKING_BASE}Booking/getAllSubCountries?Vinnumber=${formData.vin || ""}`);
        if (res.ok) {
          const data = await res.json();
          setProvinces(Array.isArray(data) ? data : []);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingProvinces(false);
      }
    };
    fetchProvinces();
  }, [formData.vin]);

  // Fetch Cities when Province changes
  useEffect(() => {
    if (!formData.provinceId) { setCities([]); return; }
    const fetchCities = async () => {
      setLoadingCities(true);
      try {
        const res = await fetch(`${BOOKING_BASE}Booking/getAllCity?SubCountryId=${formData.provinceId}&Vinnumber=${formData.vin || ""}`);
        if (res.ok) setCities(await res.json());
      } catch (e) { } finally { setLoadingCities(false); }
    };
    fetchCities();
  }, [formData.provinceId, formData.vin]);

  // Fetch Dealers when City changes
  useEffect(() => {
    if (!formData.cityId) { setDealers([]); return; }
    const fetchDealers = async () => {
      setLoadingDealers(true);
      try {
        const res = await fetch(`${BOOKING_BASE}Booking/GetAllDealer?CityId=${formData.cityId}&Vinnumber=${formData.vin || ""}`);
        if (res.ok) setDealers(await res.json());
      } catch (e) { } finally { setLoadingDealers(false); }
    };
    fetchDealers();
  }, [formData.cityId, formData.vin]);

  // Fetch Branches when Dealer changes
  useEffect(() => {
    if (!formData.dealerId) { setBranches([]); return; }
    const fetchBranches = async () => {
      setLoadingBranches(true);
      try {
        const res = await fetch(`${BOOKING_BASE}Booking/getAllBranch?DealerId=${formData.dealerId}&Vinnumber=${formData.vin || ""}`);
        if (res.ok) setBranches(await res.json());
      } catch (e) { } finally { setLoadingBranches(false); }
    };
    fetchBranches();
  }, [formData.dealerId, formData.vin]);

  // Fetch Services (ServerGroup) when Branch changes
  useEffect(() => {
    if (!formData.branchId) { setServices([]); return; }
    const fetchServices = async () => {
      setLoadingServices(true);
      try {
        const res = await fetch(`${BOOKING_BASE}Booking/getAllServerGroup?branchId=${formData.branchId}&VehicleModelId=${formData.vehicleModelId || ""}`);
        if (res.ok) setServices(await res.json());
      } catch (e) { } finally { setLoadingServices(false); }
    };
    fetchServices();
  }, [formData.branchId, formData.vehicleModelId]);

  // Fetch Dates when Service changes
  useEffect(() => {
    if (!formData.serverGroupId || !formData.branchId) { setDates([]); return; }
    const fetchDates = async () => {
      setLoadingDates(true);
      try {
        const res = await fetch(`${BOOKING_BASE}Booking/getAllServerGroupDate?ServerGroupId=${formData.serverGroupId}&BranchId=${formData.branchId}`);
        if (res.ok) setDates(await res.json());
      } catch (e) { } finally { setLoadingDates(false); }
    };
    fetchDates();
  }, [formData.serverGroupId, formData.branchId]);

  // Fetch Times when Date changes
  useEffect(() => {
    if (!formData.workShopTimeTableId || !formData.serverGroupId) { setTimes([]); return; }
    const fetchTimes = async () => {
      setLoadingTimes(true);
      try {
        const res = await fetch(`${BOOKING_BASE}Booking/getAllServerGroupTime?WorkShopTimeTableId=${formData.workShopTimeTableId}&ServerGroupId=${formData.serverGroupId}`);
        if (res.ok) setTimes(await res.json());
      } catch (e) { } finally { setLoadingTimes(false); }
    };
    fetchTimes();
  }, [formData.workShopTimeTableId, formData.serverGroupId]);

  // Fetch Advisers when Time changes
  useEffect(() => {
    if (!formData.workShopTimeTableId || !formData.serverGroupId || !formData.bookingTime) { setAdvisers([]); return; }
    const fetchAdvisers = async () => {
      setLoadingAdvisers(true);
      try {
        const res = await fetch(`${BOOKING_BASE}Booking/getAllServerGroupRepairAdvisers?WorkShopTimeTableId=${formData.workShopTimeTableId}&ServerGroupId=${formData.serverGroupId}&Time=${formData.bookingTime}`);
        if (res.ok) {
          const data = await res.json();
          setAdvisers(data);
          if (data && data.length > 0) {
            const selectedAdviser = data.length > 1 ? data[1] : data[0];
            const adviserId = selectedAdviser.UniqueId || selectedAdviser.Value;
            updateFormData({ repairAdviserId: adviserId, repairAdviserName: selectedAdviser.Text || selectedAdviser.Name || "" });
          }
        }
      } catch (e) { } finally { setLoadingAdvisers(false); }
    };
    fetchAdvisers();
  }, [formData.workShopTimeTableId, formData.serverGroupId, formData.bookingTime]);

  const handleChange = (e, resetFields = []) => {
    const { name, value, options, selectedIndex } = e.target;
    const text = selectedIndex > 0 ? options[selectedIndex].text : "";

    const updates = { [name]: value };

    // Also save the text name for submission if required by backend
    if (name === "provinceId") updates.provinceName = text;
    if (name === "cityId") updates.cityName = text;
    if (name === "dealerId") updates.dealerName = text;
    if (name === "branchId") updates.branchName = text;
    if (name === "serverGroupId") updates.serverGroupName = text;
    if (name === "workShopTimeTableId") updates.dateText = text;
    if (name === "bookingTime") updates.bookingTimeText = text; // Just in case

    // Reset child fields
    resetFields.forEach(f => {
      updates[f] = "";
      if (f.endsWith("Id")) updates[f.replace("Id", "Name")] = "";
      if (f === "workShopTimeTableId") updates.dateText = "";
    });

    updateFormData(updates);
  };

  const handleSubmit = async () => {
    // Validate
    if (!formData.branchId || !formData.serverGroupId || !formData.workShopTimeTableId || !formData.bookingTime) {
      Swal.fire({ title: "خطا", text: "لطفا تمامی موارد مربوط به نمایندگی، خدمت، تاریخ و ساعت را انتخاب کنید.", icon: "warning", confirmButtonColor: "#2b2b2b" });
      return;
    }

    setIsSubmitting(true);

    const payload = {
      SubCountryId: formData.provinceId,
      SubCountryLocalizedName: formData.provinceName,
      CityId: formData.cityId,
      CityLocalizedName: formData.cityName,
      DealerId: formData.dealerId,
      DealerName: formData.dealerName,
      BranchId: formData.branchId,
      BranchName: formData.branchName,
      ServerGroupId: formData.serverGroupId,
      ServerGroupName: formData.serverGroupName,
      ServerGroupLocalizedName: formData.serverGroupName,

      BookingFirstName: formData.firstName,
      BookingLastName: formData.lastName,
      BookingMobile: formData.mobile,
      BookingVinNumber: formData.vin,
      BookingVehicleModelId: formData.vehicleModelId,
      BookingKilometer: parseInt(formData.kilometer, 10) || 0,

      WorkShopTimeTableId: formData.workShopTimeTableId,
      BookingServerGroupId: formData.serverGroupId,
      BookingTime: formData.bookingTime,

      BookingChassisPlate: {
        PartOne: parseInt(formData.platePart1, 10) || 0,
        PartTwo: formData.plateLetter,
        PartThree: parseInt(formData.platePart2, 10) || 0,
        PartFour: parseInt(formData.plateCityCode, 10) || 0,
        PartFive: 0,
        PartSix: 0,
        FullNumberPlate: `${formData.platePart1}${formData.plateLetter}${formData.platePart2}-${formData.plateCityCode}`,
        VehicleUsageType: "Car"
      },
      NationalCode: formData.nationalCode,
      Description: formData.description,
      BookingRepairAdviserId: formData.repairAdviserId
    };

    console.log("Submitting reservation payload:", payload);

    try {
      const res = await fetch(`${BOOKING_BASE}Booking/InsertBooking`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const responseText = await res.text();
      let responseData = responseText;
      try { responseData = JSON.parse(responseText); } catch (e) { }

      console.log("Server Response:", responseData);

      if (!res.ok || (responseData && responseData.AddStatus !== undefined && responseData.AddStatus !== 0) || (responseData && responseData.Code && responseData.Code !== 0)) {
        throw new Error(responseData.Message || responseData.message || `خطای سرور: ${res.status}`);
      }

      const trackingCode = responseData?.ReturnModel?.TrackingCode || responseData?.ReturnModel?.ReversedBookingCode || responseData?.ReturnKey || "---";

      const dealerNo = responseData?.ReturnModel?.DealerNo || "---";
      const branchNo = responseData?.ReturnModel?.BranchNo || "---";
      const selectedBranch = branches.find(b => (b.UniqueId || b.Value) === formData.branchId) || {};
      const branchPhone = responseData?.ReturnModel?.BranchShopPhoneNumber || selectedBranch.BranchShopPhoneNumber || selectedBranch.TelPhone || "---";
      const branchAddress = responseData?.ReturnModel?.BookingDealerAddress || selectedBranch.RepairShopAddress || "---";
      const location = responseData?.ReturnModel?.Location ||
        ((selectedBranch.Latitude && selectedBranch.Longitude) ? `https://neshan.org/maps/@${selectedBranch.Latitude},${selectedBranch.Longitude},15z` : "---");

      const smsText = `مشتری گرامی نوبت مراجعه شما به نمایندگی ${formData.dealerName} شعبه ${formData.branchName} برساوش کد ${dealerNo}-${branchNo} در روز ${formData.dateText} ساعت ${formData.bookingTimeText || formData.bookingTime} با شماره پیگیری ${trackingCode} ثبت شد. در صورت عدم امکان مراجعه در زمان مذکور لطفا با نمایندگی هماهنگی بعمل آورید.\n شماره تماس نمایندگی: ${branchPhone}\nنشانی: ${branchAddress}\nجهت مسیر یابی، لینک زیر را لمس نمائید \n${location}                                               \nشماره مرکز پاسخگویی شرکت برساوش تجارت نیک : 0211852`;

      try {
        fetch("/api/sms/send_sms/", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            to: formData.mobile,
            message: smsText
          })
        }).catch(err => console.error("SMS fetch error:", err));
      } catch (smsError) {
        console.error("SMS sending failed:", smsError);
      }

      await Swal.fire({
        title: "نوبت شما ثبت شد ✅",
        html: `<div dir="rtl" style="text-align:right; font-family: inherit;">
          <div style="background-color: #e5e7eb; border: 1px solid #d1d5db; padding: 16px; border-radius: 12px; margin-bottom: 16px; text-align: center;">
            <h3 style="color: #111827; font-size: 18px; font-weight: bold; margin: 0 0 8px 0; text-align: center;">کد رهگیری: <span style="font-family: monospace; background: white; padding: 2px 8px; border-radius: 6px; border: 1px solid #d1d5db; display: inline-block;" dir="ltr">${trackingCode}</span></h3>
            <p style="color: #374151; font-size: 14px; font-weight: bold; margin: 0 0 4px 0;">تاریخ رزرو: <span style="color: #16a34a">${formData.dateText || "---"}</span></p>
            <p style="color: #374151; font-size: 14px; font-weight: bold; margin: 0 0 4px 0;">ساعت رزرو: <span style="color: #16a34a">${formData.bookingTimeText || formData.bookingTime || "---"}</span></p>
            <p style="color: #374151; font-size: 14px; font-weight: bold; margin: 0;">مرکز مراجعه: <span style="color: #16a34a">${formData.cityName} - ${formData.branchName}</span></p>
          </div>
          
          <table style="width: 100%; font-size: 13px; border-collapse: collapse;">
            <tbody>
              <tr style="border-bottom: 1px solid #f1f5f9;"><td style="padding: 8px 0; color: #64748b; font-weight: bold;">شماره شاسی</td><td style="padding: 8px 0; text-align: left; font-weight: bold;">${formData.vin}</td></tr>
              <tr style="border-bottom: 1px solid #f1f5f9;"><td style="padding: 8px 0; color: #64748b; font-weight: bold;">نام و نام خانوادگی</td><td style="padding: 8px 0; text-align: left; font-weight: bold;">${formData.firstName} ${formData.lastName}</td></tr>
              <tr style="border-bottom: 1px solid #f1f5f9;"><td style="padding: 8px 0; color: #64748b; font-weight: bold;">همراه</td><td style="padding: 8px 0; text-align: left; font-weight: bold;">${formData.mobile}</td></tr>
              <tr style="border-bottom: 1px solid #f1f5f9;"><td style="padding: 8px 0; color: #64748b; font-weight: bold;">کدملی/شناسه ملی</td><td style="padding: 8px 0; text-align: left; font-weight: bold;">${formData.nationalCode}</td></tr>
              <tr style="border-bottom: 1px solid #f1f5f9;"><td style="padding: 8px 0; color: #64748b; font-weight: bold;">پلاک خودرو</td><td style="padding: 8px 0; text-align: left; font-weight: bold;" dir="ltr">${formData.platePart1} ${formData.plateLetter} ${formData.platePart2} - ${formData.plateCityCode}</td></tr>
              <tr style="border-bottom: 1px solid #f1f5f9;"><td style="padding: 8px 0; color: #64748b; font-weight: bold;">نمایندگی</td><td style="padding: 8px 0; text-align: left; font-weight: bold;">${formData.dealerName}</td></tr>
              <tr style="border-bottom: 1px solid #f1f5f9;"><td style="padding: 8px 0; color: #64748b; font-weight: bold;">شعبه</td><td style="padding: 8px 0; text-align: left; font-weight: bold;">${formData.branchName}</td></tr>
              <tr style="border-bottom: 1px solid #f1f5f9;"><td style="padding: 8px 0; color: #64748b; font-weight: bold;">خدمات</td><td style="padding: 8px 0; text-align: left; font-weight: bold;">${formData.serverGroupName}</td></tr>
              <tr style="border-bottom: 1px solid #f1f5f9;"><td style="padding: 8px 0; color: #64748b; font-weight: bold;">آدرس</td><td style="padding: 8px 0; text-align: left; font-weight: bold;">${formData.cityName}</td></tr>
            </tbody>
          </table>
        </div>`,
        icon: "success",
        width: 600,
        confirmButtonText: "رزرو نوبت جدید",
        confirmButtonColor: "#2b2b2b",
        allowOutsideClick: false
      }).then(() => {
        window.location.reload();
      });

    } catch (err) {
      Swal.fire({ title: "خطا!", text: err.message || "ارتباط با سرور برقرار نشد.", icon: "error", confirmButtonText: "متوجه شدم", confirmButtonColor: "#2b2b2b" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-full animate-in fade-in slide-in-from-right-4 duration-500">
      <div className="flex items-center gap-3 mb-6 border-b border-slate-100 pb-4">
        <div className="bg-gray-100 p-2.5 rounded-xl text-[#2b2b2b]">
          <Building2 size={24} />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-800">انتخاب نمایندگی و زمان</h2>
          <p className="text-sm text-slate-500 mt-1">
            نمایندگی، نوع خدمت و زمان مراجعه خود را انتخاب کنید
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar pb-6 space-y-4">

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-slate-700 flex justify-between">
              استان
              {loadingProvinces && <span className="text-xs text-[#2b2b2b] animate-pulse">در حال دریافت...</span>}
            </label>
            <select
              name="provinceId"
              value={formData.provinceId}
              onChange={(e) => handleChange(e, ["cityId", "dealerId", "branchId", "serverGroupId", "workShopTimeTableId", "bookingTime"])}
              disabled={loadingProvinces}
              className="w-full px-4 py-3 rounded-xl border-2 text-sm bg-white border-slate-200 focus:border-[#2b2b2b] focus:ring-1 focus:ring-[#2b2b2b] outline-none disabled:bg-slate-100 disabled:opacity-70"
            >
              <option value="">انتخاب کنید</option>
              {provinces.filter(p => (p.UniqueId || p.Value) !== "346f9259-e50c-4d10-8154-3433488c95cd").map((p, i) => <option key={p.UniqueId || p.Value || i} value={p.UniqueId || p.Value}>{p.SubCountryLocalizedName || p.Text || p.Name}</option>)}
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-slate-700 flex justify-between">
              شهر
              {loadingCities && <span className="text-xs text-[#2b2b2b] animate-pulse">در حال دریافت...</span>}
            </label>
            <select
              name="cityId"
              value={formData.cityId}
              onChange={(e) => handleChange(e, ["dealerId", "branchId", "serverGroupId", "workShopTimeTableId", "bookingTime"])}
              disabled={!formData.provinceId || loadingCities}
              className="w-full px-4 py-3 rounded-xl border-2 text-sm bg-white border-slate-200 focus:border-[#2b2b2b] focus:ring-1 focus:ring-[#2b2b2b] outline-none disabled:bg-slate-100 disabled:opacity-70"
            >
              <option value="">انتخاب کنید</option>
              {cities.filter(c => (c.UniqueId || c.Value) !== "346f9259-e50c-4d10-8154-3433488c95cd").map((c, i) => <option key={c.UniqueId || c.Value || i} value={c.UniqueId || c.Value}>{c.CityLocalizedName || c.Text || c.Name}</option>)}
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-slate-700 flex justify-between">
              نمایندگی
              {loadingDealers && <span className="text-xs text-[#2b2b2b] animate-pulse">در حال دریافت...</span>}
            </label>
            <select
              name="dealerId"
              value={formData.dealerId}
              onChange={(e) => handleChange(e, ["branchId", "serverGroupId", "workShopTimeTableId", "bookingTime"])}
              disabled={!formData.cityId || loadingDealers}
              className="w-full px-4 py-3 rounded-xl border-2 text-sm bg-white border-slate-200 focus:border-[#2b2b2b] focus:ring-1 focus:ring-[#2b2b2b] outline-none disabled:bg-slate-100 disabled:opacity-70"
            >
              <option value="">انتخاب کنید</option>
              {dealers.filter(d => (d.UniqueId || d.Value) !== "346f9259-e50c-4d10-8154-3433488c95cd").map((d, i) => <option key={d.UniqueId || d.Value || i} value={d.UniqueId || d.Value}>{d.DealerLocalizedName || d.DealerName || d.Text || d.Name}</option>)}
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-slate-700 flex justify-between">
              شعبه
              {loadingBranches && <span className="text-xs text-[#2b2b2b] animate-pulse">در حال دریافت...</span>}
            </label>
            <select
              name="branchId"
              value={formData.branchId}
              onChange={(e) => handleChange(e, ["serverGroupId", "workShopTimeTableId", "bookingTime"])}
              disabled={!formData.dealerId || loadingBranches}
              className="w-full px-4 py-3 rounded-xl border-2 text-sm bg-white border-slate-200 focus:border-[#2b2b2b] focus:ring-1 focus:ring-[#2b2b2b] outline-none disabled:bg-slate-100 disabled:opacity-70"
            >
              <option value="">انتخاب کنید</option>
              {branches.filter(b => (b.UniqueId || b.Value) !== "346f9259-e50c-4d10-8154-3433488c95cd").map((b, i) => <option key={b.UniqueId || b.Value || i} value={b.UniqueId || b.Value}>{b.BranchLocalizedName || b.BranchName || b.Text || b.Name}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 border-t border-slate-100 pt-5 mt-5">
          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-slate-700 flex justify-between">
              نوع خدمات
              {loadingServices && <span className="text-xs text-[#2b2b2b] animate-pulse">در حال دریافت...</span>}
            </label>
            <select
              name="serverGroupId"
              value={formData.serverGroupId}
              onChange={(e) => handleChange(e, ["workShopTimeTableId", "bookingTime"])}
              disabled={!formData.branchId || loadingServices}
              className="w-full px-4 py-3 rounded-xl border-2 text-sm bg-white border-slate-200 focus:border-[#2b2b2b] focus:ring-1 focus:ring-[#2b2b2b] outline-none disabled:bg-slate-100 disabled:opacity-70"
            >
              <option value="">انتخاب کنید</option>
              {services.filter(s => (s.UniqueId || s.Value) !== "346f9259-e50c-4d10-8154-3433488c95cd").map((s, i) => <option key={s.UniqueId || s.Value || i} value={s.UniqueId || s.Value}>{s.ServerGroupLocalizedName || s.ServerGroupName || s.Text || s.Name}</option>)}
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-slate-700 flex justify-between">
              تاریخ مراجعه
              {loadingDates && <span className="text-xs text-[#2b2b2b] animate-pulse">در حال دریافت...</span>}
            </label>
            <select
              name="workShopTimeTableId"
              value={formData.workShopTimeTableId}
              onChange={(e) => handleChange(e, ["bookingTime"])}
              disabled={!formData.serverGroupId || loadingDates}
              className="w-full px-4 py-3 rounded-xl border-2 text-sm bg-white border-slate-200 focus:border-[#2b2b2b] focus:ring-1 focus:ring-[#2b2b2b] outline-none disabled:bg-slate-100 disabled:opacity-70"
            >
              <option value="">انتخاب کنید</option>
              {dates.filter(d => (d.UniqueId || d.Value) !== "346f9259-e50c-4d10-8154-3433488c95cd").map((d, i) => <option key={d.UniqueId || d.Value || i} value={d.UniqueId || d.Value}>{d.Text || d.Name || d.WorkShopTimeTableDate}</option>)}
            </select>
          </div>

          <div className="space-y-1.5 sm:col-span-2 lg:col-span-1">
            <label className="text-sm font-semibold text-slate-700 flex justify-between">
              ساعت مراجعه
              {loadingTimes && <span className="text-xs text-[#2b2b2b] animate-pulse">در حال دریافت...</span>}
            </label>
            <select
              name="bookingTime"
              value={formData.bookingTime}
              onChange={(e) => handleChange(e, [])}
              disabled={!formData.workShopTimeTableId || loadingTimes}
              className="w-full px-4 py-3 rounded-xl border-2 text-sm bg-white border-slate-200 focus:border-[#2b2b2b] focus:ring-1 focus:ring-[#2b2b2b] outline-none disabled:bg-slate-100 disabled:opacity-70"
            >
              <option value="">انتخاب کنید</option>
              {times.filter(t => (t.UniqueId || t.Value) !== "346f9259-e50c-4d10-8154-3433488c95cd").map((t, i) => <option key={t.UniqueId || t.Value || i} value={t.UniqueId || t.Value}>{t.Text || t.Name || t.BookingTime}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="mt-6 flex flex-col-reverse sm:flex-row items-center justify-between gap-4 border-t border-slate-100 pt-6">
        <button onClick={onPrev} className="w-full sm:w-auto flex items-center justify-center gap-2 py-3 px-6 rounded-xl font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-all">
          <ArrowRight size={18} /> قبلی
        </button>
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="w-full sm:w-auto flex items-center justify-center gap-2 py-3.5 px-8 rounded-xl font-bold text-white bg-[#2b2b2b] hover:bg-neutral-800 shadow-lg shadow-neutral-900/20 transition-all active:scale-95 disabled:opacity-70 disabled:scale-100"
        >
          {isSubmitting ? (
            <><Loader2 size={18} className="animate-spin" /> در حال ثبت...</>
          ) : (
            <>ثبت نهایی نوبت <Send size={18} /></>
          )}
        </button>
      </div>
    </div>
  );
}
